import * as S from '../../../components/GridComponent';
import { colors } from '~/theme';
import { formatDollars } from '~/utils';
import { display } from '~/utils/displayValues/common';
import { UnitDecimals } from '~/constants';

interface BudgetTableProps {
  values: any;
  modalPricingType?: 'ACQUISITION' | 'OFFTAKE' | 'ITC' | 'DEV_FEE' | 'BRIDGE_LOAN';
  bidSubmitDisplay?: number;
  isConfirmBidModal: boolean;
}

export default function BudgetTable({ 
  values,
  modalPricingType, 
  bidSubmitDisplay, 
  isConfirmBidModal 
}: BudgetTableProps) {
  
  const budget = !isConfirmBidModal ? values?.budget : values;

  const epcCost = budget?.epcCost?.perWdc;
  const epcCostTotal = budget?.epcCost?.total;
  const interconnCost = budget?.interconnection?.perWdc;
  const interconnTotal = budget?.interconnection?.total;
  const otherItcCost = budget?.otherItc?.perWdc;
  const otherItcTotal = budget?.otherItc?.total;
  const otherNonItcCost = budget?.otherNonItc?.perWdc;
  const otherNonItcTotal = budget?.otherNonItc?.total;
  const conductorFee = budget?.conductorSolarFee?.perWdc;
  const conductorFeeTotal = budget?.conductorSolarFee?.total;
  const totalPerWdc = budget?.totals?.perWdc;
  const total = budget?.totals?.total;

  const displayConductorFee = budget?.conductorSolarFee?.isDisplaying;
  const isHighlighted = budget?.epcCost?.isHighlighted;

  const bidModalOfftake = display(bidSubmitDisplay && formatDollars(bidSubmitDisplay, { dec: UnitDecimals.KWH, default0: 'TBD' }));

  return (
    <S.GridContainer>

      <S.SubGrid 
        columnValues= {isConfirmBidModal ? '1.5fr 1fr 1fr' : '11rem 6rem 6rem' }
        gridColumn='1 / 4' 
        style={{paddingBottom: 'var(--small)'}}
      >
        <div className="title">Value</div>
        <strong>$ per Wdc</strong>
        <strong>Total amount</strong>
      </S.SubGrid> 

      <S.GridTable 
        columnValues= {isConfirmBidModal ? '1.5fr 1fr 1fr' : '11rem 6rem 6rem' }
        gridColumn='1 / 4'
      >
        <S.GridLabelEl style={{color: modalPricingType === 'ACQUISITION' ? colors.orange : colors.charcoal}}>
          {modalPricingType === 'ACQUISITION' ? 'EPC cost + dev fee - your bid' : 'EPC cost + dev fee'}
        </S.GridLabelEl>
        <S.GridTableEl 
          gridColumn='2 / 3' 
          textAlign='right'
          style={{ 
            color: isHighlighted || modalPricingType === 'ACQUISITION' ? colors.orange : colors.pureBlack, 
            fontWeight: isHighlighted || modalPricingType === 'ACQUISITION' ? 'bold' : 'normal'
          }}
        >
          {epcCost}
        </S.GridTableEl>
        <S.GridTableEl 
          gridColumn='3 / 4' 
          textAlign='right'
          style={{ 
            color: isHighlighted || modalPricingType === 'ACQUISITION' ? colors.orange : colors.pureBlack, 
            fontWeight: isHighlighted || modalPricingType === 'ACQUISITION' ? 'bold' : 'normal'
          }}
        >
          {epcCostTotal}
        </S.GridTableEl>

        <S.GridLabelEl>Interconnection</S.GridLabelEl>
        <S.GridTableEl gridColumn='2 / 3' textAlign='right'>{interconnCost}</S.GridTableEl>
        <S.GridTableEl gridColumn='3 / 4' textAlign='right'>{interconnTotal}</S.GridTableEl>

        <S.GridLabelEl>Other ITC-eligible costs</S.GridLabelEl>
        <S.GridTableEl gridColumn='2 / 3' textAlign='right'>{otherItcCost}</S.GridTableEl>
        <S.GridTableEl gridColumn='3 / 4' textAlign='right'>{otherItcTotal}</S.GridTableEl>
        
        <S.GridLabelEl>Non-ITC eligible costs</S.GridLabelEl>
        <S.GridTableEl gridColumn='2 / 3' textAlign='right'>{otherNonItcCost}</S.GridTableEl>
        <S.GridTableEl gridColumn='3 / 4' textAlign='right'>{otherNonItcTotal}</S.GridTableEl>

        {displayConductorFee ? (
        <>   
          <S.GridLabelEl>Conductor Solar fee</S.GridLabelEl>
          <S.GridTableEl gridColumn='2 / 3' textAlign='right'>{conductorFee}</S.GridTableEl>
          <S.GridTableEl gridColumn='3 / 4' textAlign='right'>{conductorFeeTotal}</S.GridTableEl>
        </>
        ) : null}

        {isConfirmBidModal && (
          <S.GridLabelEl 
          gridColumn='1 / 4'
          style={{
            padding: '0',
            marginBottom: 'var(--x-small)',
            borderBottom: '1px solid var(--color-charcoal)'
          }} />
        )}

        <S.GridLabelEl
          gridColumn='1 / 2' 
          className="total" 
        >
          {isConfirmBidModal ? 'Total cost to you': 'Project Totals'}
        </S.GridLabelEl>
        <S.GridTableEl 
          gridColumn='2 / 3' 
          textAlign='right'
          className="total"
        >
          <strong>{totalPerWdc}</strong>
        </S.GridTableEl>
        <S.GridTableEl 
          gridColumn='3 / 4' 
          textAlign='right'
          className="total"
        >
          <strong>{total}</strong>
        </S.GridTableEl>

        {modalPricingType === 'OFFTAKE' && (  
          <>
            <S.GridLabelEl
              gridColumn='1 / 2'
              style={{
                color: 'var(--color-primary)', 
                fontWeight: '700',
                marginTop: 'var(--small)'
              }}
            >
              Offtake rate - your bid
            </S.GridLabelEl>

            <S.GridTableEl 
              gridColumn='2 / 3' 
              textAlign='right'
              style={{
                color: 'var(--color-primary)', 
                fontWeight: '700',
                marginTop: 'var(--small)'
              }}
            >
            {`${bidModalOfftake} / kWh`} 
            </S.GridTableEl>
          </>
        )}
      </S.GridTable>
    </S.GridContainer>
  );
};